import { ofType } from 'redux-observable';
import { Observable, Observer, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { FeatureFlagsService } from '../../services/feature-flags/FeatureFlags.service';
import { closeFlagStream, openFlagStream } from '../../services/feature-flags/FeatureFlagsStream.service';
import { LoggerService } from '../../services/logger/Logger.service';
import FeatureFlagState from '../../store-util/FeatureFlagState';
import { applyFlagEvent, resolveFlagEnvironment, toFeatureFlagMap } from '../../util/flags/helpers';
import { setCookieSessionValid } from '../auth';
import { individualExtendedInfoSuccess, logout } from '../common-actions';

import { featureFlagsLoaded, featureFlagUpdated, resetFeatureFlags } from './index';

const logger = LoggerService.getLogger();

// The fetch cannot be aborted, so the returned teardown marks this load as
// cancelled: a response that arrives after switchMap moved on (logout, or a
// re-trigger) must not open a stream nothing will ever close.
const loadAndStream = (observer: Observer<any>): (() => void) => {
  const environment = resolveFlagEnvironment();
  let cancelled = false;

  FeatureFlagsService.getInstance()
    .getFlags(environment)
    .then(response => {
      if (cancelled) return;

      if (!response.ok || !response.data?.flags) {
        logger.error('[flags] Feature flags request failed', response.status);
        return;
      }

      let flags = toFeatureFlagMap(response.data.flags);
      FeatureFlagState.set(flags);
      observer.next(featureFlagsLoaded(flags));

      openFlagStream(environment, response.data.streamCursor, event => {
        flags = applyFlagEvent(flags, event);
        FeatureFlagState.set(flags);
        observer.next(featureFlagUpdated(event));
      });
    })
    .catch(error => logger.error('[flags] Feature flags request threw', error));

  return () => {
    cancelled = true;
    closeFlagStream();
  };
};

// individualExtendedInfoSuccess also re-triggers the load: the boot-time cookie
// check can fail before the session is restored, and a succeeded CRM call proves
// the session works again.
const featureFlagsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(setCookieSessionValid.type, individualExtendedInfoSuccess.type, logout.type),
  switchMap(action => {
    const isSessionInvalidated = action.type === logout.type
      || (action.type === setCookieSessionValid.type && action.payload !== true);

    if (isSessionInvalidated) {
      FeatureFlagState.reset();
      return of(resetFeatureFlags());
    }

    return new Observable((observer: Observer<any>) => loadAndStream(observer));
  }),
);

export default featureFlagsEpic;
